import React, { useState } from 'react';
import { Query, Mutation } from 'react-apollo';
import gql from 'graphql-tag';
import { navigate } from '@reach/router';
import Select from 'react-select';
import { Button } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import { GET_PRODUCT, GET_MY_PRODUCTS } from '../../queries';
import classes from './edit-product.module.scss';
import allCategories from '../add-product/categories';
import { uploadFiles } from '../add-product';
import Input from '../input/input';
import Error from '../common/error';
import { Formik } from 'formik';
import * as Yup from 'yup';
import ImagesPicker from './images-picker';
import { MAX_IMAGE_FILE_SIZE } from '../../constants';
import TextArea from '../common/textarea';

const CHANGE_PRODUCT = gql`
  mutation changeProduct(
    $id: String!
    $name: String
    $description: String
    $price: Float
    $active: Boolean
    $imagesUrls: [String]
    $categories: [CategoryInput]
  ) {
    changeProduct(
      id: $id
      name: $name
      description: $description
      price: $price
      active: $active
      imagesUrls: $imagesUrls
      categories: $categories
    )
  }
`;

const EditProduct = ({ id }: any): any => {
  const [showSpinner, setShowSpinner] = useState(false);
  const [errorMessage, setError] = useState('');

  const saveChanges = (values, changeProductMutation) => async () => {
    setShowSpinner(true);
    let newImagesUrls: string[] = [];
    try {
      const imagesToUpload = values.images
        .filter(image => image.base64)
        .map(image => image.file);
      if (imagesToUpload) {
        const filesInfo = await uploadFiles(Array.from(imagesToUpload));
        newImagesUrls = filesInfo.map(
          (info: { secure_url: string }) => info.secure_url
        );
      }
    } catch (e) {
      setShowSpinner(false);
      setError(e.message);
      return;
    }
    try {
      await changeProductMutation({
        variables: {
          id,
          name: values.name,
          description: values.description,
          price: values.price,
          active: values.active,
          categories: values.categories,
          imagesUrls: [
            ...values.images.filter(image => !image.base64),
            ...newImagesUrls
          ]
        },
        refetchQueries: [
          { query: GET_MY_PRODUCTS },
          { query: GET_PRODUCT, variables: { id } }
        ]
      });
      navigate('/manage-products');
    } catch (e) {
      setError(e.message);
    }
    setShowSpinner(false);
  };

  return (
    <Query query={GET_PRODUCT} variables={{ id }}>
      {({ loading, error, data }) => {
        if (loading) return <div>Loading...</div>;
        if (error) return <div>Error</div>;
        if (!data.product) {
          return <div>No such product</div>;
        }
        const {
          product: {
            imagesUrls: images,
            name,
            description,
            price,
            active,
            categories
          }
        } = data;

        return (
          <Formik
            initialValues={{
              name,
              description,
              price,
              images,
              active,
              categories
            }}
            onSubmit={() => true}
            validationSchema={Yup.object().shape({
              name: Yup.string().required('Вкажіть назву товару'),
              description: Yup.string().required('Додайте опис товару'),
              price: Yup.number().required('Вкажіть ціну'),
              images: Yup.array()
                .required('Виберіть фото')
                .test(
                  'is-file-bigger-than-1mb',
                  'Завеликий розмір файлу(ів)',
                  files => {
                    const newImages = files.filter(
                      file => typeof file !== 'string'
                    );
                    return !(
                      newImages.filter(f => f.file.size < MAX_IMAGE_FILE_SIZE)
                        .length < newImages.length
                    );
                  }
                ),
              categories: Yup.array().required('Вкажіть категорії')
            })}
          >
            {props => {
              const {
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                setFieldValue,
                setFieldTouched,
                isValid
              } = props;

              return (
                <div style={{ marginTop: '10px' }}>
                  {errorMessage && <Error errorMessage={errorMessage} />}
                  <div className={classes['edit-item']}>
                    <Input
                      type="text"
                      name="name"
                      error={touched.name && errors.name}
                      value={values.name}
                      label="Назва "
                      changed={handleChange}
                      unfocused={handleBlur}
                    />
                  </div>
                  <div className={classes['edit-item']}>
                    <TextArea
                      name="description"
                      error={touched.description && errors.description}
                      value={values.description}
                      label="Опис "
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </div>
                  <div className={classes['edit-item']}>
                    <Input
                      type="number"
                      name="price"
                      value={values.price}
                      error={touched.price && errors.price}
                      label="Ціна "
                      changed={handleChange}
                      unfocused={handleBlur}
                    />
                  </div>
                  <div className={classes['edit-item']}>
                    <span className={classes.label}>Категорії:</span>
                    <div style={{ flex: 1 }}>
                      <Select
                        options={allCategories}
                        value={values.categories}
                        onChange={(selectedCategories: any) => {
                          setFieldValue('categories', selectedCategories);
                          setFieldTouched('categories', true);
                        }}
                        placeholder="Виберіть категорію(ї)"
                        isMulti
                      />
                    </div>
                  </div>
                  <div className={classes['edit-item']}>
                    <label>
                      <input
                        type="checkbox"
                        checked={values.active}
                        onChange={() => {
                          setFieldValue('active', !values.active);
                        }}
                      />
                      Показувати продукт
                    </label>
                  </div>
                  <div
                    style={{
                      border: errors.images ? '2px solid #fc1010' : 'none',
                      padding: '5px'
                    }}
                  >
                    <ImagesPicker
                      images={values.images}
                      onChange={newImages => {
                        setFieldValue('images', newImages);
                      }}
                    />
                  </div>
                  {errors.images && (
                    <p style={{ color: 'red' }}>{errors.images}</p>
                  )}

                  <Mutation mutation={CHANGE_PRODUCT}>
                    {changeProductMutation => (
                      <Button
                        variant="contained"
                        color="primary"
                        data-e2e-id={'save-button'}
                        disabled={!isValid}
                        onClick={saveChanges(values, changeProductMutation)}
                      >
                        Зберегти
                        {showSpinner && (
                          <CircularProgress
                            size={20}
                            style={{ marginLeft: '10px', color: '#fff' }}
                          />
                        )}
                      </Button>
                    )}
                  </Mutation>
                </div>
              );
            }}
          </Formik>
        );
      }}
    </Query>
  );
};
export default EditProduct;
